import React from "react";
import { Marker, Popup } from "react-map-gl/mapbox";
import Image from "next/image";
import { LocationCard } from "../location-card";
import { ILocation } from "@/services/locationApi";
import { LocationCluster } from "@/lib/stateClusters";
import byLogo from "@/assets/brokenyolklogo.svg";

export function ClusterMarker({
  cluster,
  size = "md",
  onZoomIn,
  zoomInLevel=6,
}: {
  cluster: LocationCluster;
  size?: "md" | "lg";
  onZoomIn: (cluster: LocationCluster, zoomInLevel: number) => void;
  zoomInLevel?: number 
}) {
  const sizeClass = size === "lg" ? "w-10 h-10 md:w-[60px] md:h-[60px]" : "w-8 h-8 md:w-14 md:h-14";
  const fontClass = size === "lg" ? "text-[18px] md:text-[24px]" : "text-[16px] md:text-[22px]";

  return (
    <Marker
      longitude={cluster.lng}
      latitude={cluster.lat}
      anchor="center"
      onClick={() => onZoomIn(cluster, zoomInLevel)}
    >
      <div className={`relative ${sizeClass} rounded-full bg-secondary shadow-md cursor-pointer hover:scale-110 transition-transform flex items-center justify-center`}>
        <span className={`font-[Righteous-Regular] ${fontClass} leading-none text-black`}>
          {cluster.count}
        </span>
      </div>
    </Marker>
  );
}

export function LocationMarker({
  location,
  isSelected,
  onSelect,
}: {
  location: ILocation;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <Marker
      longitude={location.fieldData.longitude}
      latitude={location.fieldData.latitude}
      anchor="center"
      onClick={(e) => {
        e.originalEvent.stopPropagation();
        onSelect();
      }}
    >
      <div
        className={`w-8 h-8 md:w-10 md:h-10 rounded-full bg-secondary flex items-center justify-center cursor-pointer shadow-md overflow-hidden transition-transform ${
          isSelected ? "scale-125" : "hover:scale-110"
        }`}
        title={location.fieldData.name}
      >
        <Image
          src={byLogo}
          alt={location.fieldData.name}
          width={24}
          height={24}
          className="object-contain"
        />
      </div>
    </Marker>
  );
}

export function LocationPopup({
  location,
  onClose,
}: {
  location: ILocation;
  onClose: () => void;
}) {
  return (
    <Popup
      longitude={location.fieldData.longitude!}
      latitude={location.fieldData.latitude!}
      anchor="top"
      closeButton={false}
      closeOnClick={false}
      onClose={onClose}
      maxWidth="360px"
    >
      <div className="relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-[#0b1c24] shadow-sm transition-colors cursor-pointer"
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <LocationCard location={location} compact />
      </div>
    </Popup>
  );
}
